<template>
  <div class="w-full max-w-sm mx-auto select-none font-sans">
    <!-- 顶栏计分与操作 -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-1.5">
        <button
          @click="resetGame"
          type="button"
          class="inline-flex items-center justify-center h-10 px-3.5 rounded-lg bg-primary text-white text-sm font-semibold shadow hover:opacity-90 active:scale-95 transition cursor-pointer"
        >
          新游戏
        </button>
        <button
          type="button"
          @click="leaderboardVisible = true"
          class="inline-flex items-center justify-center h-10 px-3 rounded-lg border border-amber-800/40 bg-amber-50 text-sm font-semibold text-amber-900 hover:bg-amber-100 transition cursor-pointer font-serif gap-1 shadow-2xs"
          title="查看 2048 全网高分金榜"
        >
          <span>🏆</span>
          <span>榜单</span>
        </button>
        <button
          type="button"
          @click="shareScore"
          class="inline-flex items-center justify-center h-10 px-2.5 rounded-lg border border-border bg-white text-sm font-semibold text-clay hover:bg-secondary transition cursor-pointer"
        >
          分享
        </button>
      </div>

      <div class="flex gap-2">
        <div class="bg-secondary/80 rounded-lg px-2.5 py-1.5 text-center min-w-[4rem] border border-border">
          <div class="text-[10px] uppercase font-bold text-muted tracking-wider">得分</div>
          <div class="text-base sm:text-lg font-bold text-clay leading-tight">{{ score }}</div>
        </div>
        <div class="bg-secondary/80 rounded-lg px-2.5 py-1.5 text-center min-w-[4rem] border border-border">
          <div class="text-[10px] uppercase font-bold text-muted tracking-wider">最高纪录</div>
          <div class="text-base sm:text-lg font-bold text-seal leading-tight">{{ bestScore }}</div>
        </div>
      </div>
    </div>

    <!-- 游戏棋盘容器 -->
    <div
      ref="boardRef"
      class="relative aspect-square w-full rounded-2xl bg-[#bbada0] p-3 shadow-inner touch-none"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <!-- 4x4 底格网格 -->
      <div class="grid grid-cols-4 grid-rows-4 gap-2.5 h-full w-full">
        <div
          v-for="i in 16"
          :key="i"
          class="rounded-xl bg-[#cdc1b4]/70 flex items-center justify-center"
        ></div>
      </div>

      <!-- 实际数字方块图层 -->
      <div class="absolute inset-0 p-3 grid grid-cols-4 grid-rows-4 gap-2.5 pointer-events-none">
        <template v-for="(row, r) in board" :key="r">
          <div
            v-for="(val, c) in row"
            :key="`${r}-${c}`"
            class="flex items-center justify-center"
          >
            <div
              v-if="val > 0"
              class="w-full h-full rounded-xl flex items-center justify-center font-bold transition-all duration-100 transform"
              :class="[getTileClass(val), val > 512 ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl']"
            >
              {{ val }}
            </div>
          </div>
        </template>
      </div>

      <!-- 游戏结束浮层 -->
      <div
        v-if="isGameOver"
        class="absolute inset-0 bg-black/60 backdrop-blur-xs rounded-2xl flex flex-col items-center justify-center p-6 text-center z-20 animate-fade-in"
      >
        <div class="text-3xl font-extrabold text-white mb-2">游戏结束</div>
        <p class="text-sm text-white/80 mb-5">棋盘已满无路可走，最终得分 {{ score }}</p>
        <div class="flex flex-wrap justify-center gap-2.5">
          <button
            type="button"
            @click="leaderboardVisible = true"
            class="h-10 px-3.5 rounded-lg bg-amber-500 hover:bg-yellow-400 text-stone-900 font-bold text-sm shadow transition flex items-center gap-1 cursor-pointer"
          >
            <span>🏆</span>
            <span>看排行榜</span>
          </button>
          <button
            type="button"
            @click="shareScore"
            class="h-10 px-3.5 rounded-lg bg-white text-clay font-bold text-sm shadow hover:bg-white/90 transition cursor-pointer"
          >
            炫耀战绩
          </button>
          <button
            @click="resetGame"
            class="h-10 px-5 rounded-lg bg-primary text-white font-bold text-sm shadow-lg hover:opacity-90 active:scale-95 transition cursor-pointer"
          >
            再试一次
          </button>
        </div>
      </div>

      <!-- 达到 2048 胜利弹层 -->
      <div
        v-if="isWon && !keepPlaying"
        class="absolute inset-0 bg-yellow-500/80 backdrop-blur-xs rounded-2xl flex flex-col items-center justify-center p-6 text-center z-20 animate-fade-in"
      >
        <div class="text-3xl font-extrabold text-white mb-2">恭喜达成 2048！</div>
        <p class="text-sm text-white/90 mb-5">你的空间规划与计算推演能力令人赞叹</p>
        <div class="flex flex-wrap justify-center gap-2.5">
          <button
            type="button"
            @click="leaderboardVisible = true"
            class="h-10 px-3.5 rounded-lg bg-stone-900 text-white font-bold text-sm shadow hover:bg-stone-800 transition flex items-center gap-1 cursor-pointer"
          >
            <span>🏆</span>
            <span>看排行榜</span>
          </button>
          <button
            type="button"
            @click="shareScore"
            class="h-10 px-3.5 rounded-lg bg-white text-clay font-bold text-sm shadow hover:bg-white/90 transition cursor-pointer"
          >
            炫耀战绩
          </button>
          <button
            @click="keepPlaying = true"
            class="h-10 px-3.5 rounded-lg bg-white text-clay font-bold text-sm shadow hover:bg-white/90 transition cursor-pointer"
          >
            继续挑战
          </button>
          <button
            @click="resetGame"
            class="h-10 px-4 rounded-lg bg-primary text-white font-bold text-sm shadow hover:opacity-90 transition cursor-pointer"
          >
            重新开始
          </button>
        </div>
      </div>
    </div>

    <!-- 底部控制提示 -->
    <div class="mt-4 text-center text-xs text-muted flex items-center justify-center gap-4">
      <span>电脑端：按键盘 <strong>↑ ↓ ← →</strong> 或 <strong>W A S D</strong></span>
      <span>移动端：触屏任意方向滑动</span>
    </div>

    <!-- 2048 排行榜抽屉 -->
    <GameLeaderboardDrawer
      :visible="leaderboardVisible"
      :game-code="'game_2048'"
      @close="leaderboardVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { trackLevelStart } from '@/analytics/track'
import { share2048 } from '@/vue-apps/share/games'
import { checkpoint, getPlayerProgress } from '@/api/game'
import GameLeaderboardDrawer from '@/components/game/GameLeaderboardDrawer.vue'

const STORAGE_KEY = 'miyucai_2048_best'

// 排行榜抽屉与对局步数、耗时
const leaderboardVisible = ref(false)
let moveCount = 0
let gameStartTime = Date.now()

// 提交成绩到服务端排行榜
const submitScore = async (finalScore: number) => {
  if (finalScore <= 0) return
  const elapsedMs = Math.max(Date.now() - gameStartTime, 500)
  const steps = Math.max(moveCount, 1)
  const attemptId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : 'att_2048_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)

  try {
    await checkpoint('game_2048', {
      attemptId,
      levelId: finalScore,
      elapsedMs,
      stepsCount: steps,
      manifestVersion: 'v1'
    })
  } catch (err) {
    console.warn('2048 score submission failed:', err)
  }
}

// 棋盘数据 (4x4)
const board = ref<number[][]>([
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0, 0, 0, 0],
])

const score = ref(0)

const shareScore = () => {
  share2048(score.value)
}
const bestScore = ref(0)
const isGameOver = ref(false)
const isWon = ref(false)
const keepPlaying = ref(false)

// 触控滑动记录
let touchStartX = 0
let touchStartY = 0

// 初始化与本地最高分读取
const initBestScore = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      bestScore.value = parseInt(saved, 10) || 0
    }
  } catch (e) {
    // 忽略私密模式禁用 localStorage 错误
  }
}

const updateBestScore = () => {
  if (score.value > bestScore.value) {
    bestScore.value = score.value
    try {
      localStorage.setItem(STORAGE_KEY, String(bestScore.value))
    } catch (e) {
      // 容错保护
    }
  }
}

// 在空余格子里随机放置一个 2 (90%) 或 4 (10%)
const addRandomTile = () => {
  const emptyCells: { r: number; c: number }[] = []
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      if (board.value[r][c] === 0) {
        emptyCells.push({ r, c })
      }
    }
  }

  if (emptyCells.length === 0) return

  const randomCell = emptyCells[Math.floor(Math.random() * emptyCells.length)]
  board.value[randomCell.r][randomCell.c] = Math.random() < 0.9 ? 2 : 4
}

// 重置游戏
const resetGame = () => {
  if (score.value > 0) {
    submitScore(score.value)
  }
  moveCount = 0
  gameStartTime = Date.now()
  board.value = [
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
  ]
  score.value = 0
  isGameOver.value = false
  isWon.value = false
  keepPlaying.value = false

  addRandomTile()
  addRandomTile()
  trackLevelStart({ level_name: '2048' })
}

// 单行向左合并
const slideAndMergeRow = (row: number[]): { newRow: number[]; gainedScore: number; moved: boolean } => {
  // 1. 过滤 0
  const filtered = row.filter((val) => val !== 0)
  const newRow: number[] = []
  let gainedScore = 0

  for (let i = 0; i < filtered.length; i++) {
    if (i + 1 < filtered.length && filtered[i] === filtered[i + 1]) {
      const mergedVal = filtered[i] * 2
      newRow.push(mergedVal)
      gainedScore += mergedVal
      if (mergedVal === 2048 && !isWon.value) {
        isWon.value = true
      }
      i++ // 跳过已合并的下一个
    } else {
      newRow.push(filtered[i])
    }
  }

  // 补 0 到长度 4
  while (newRow.length < 4) {
    newRow.push(0)
  }

  // 检查是否有位移或变动
  let moved = false
  for (let i = 0; i < 4; i++) {
    if (newRow[i] !== row[i]) {
      moved = true
      break
    }
  }

  return { newRow, gainedScore, moved }
}

// 顺时针旋转 90 度
const rotateBoard = (b: number[][]): number[][] => {
  const res: number[][] = [
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
    [0, 0, 0, 0],
  ]
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      res[c][3 - r] = b[r][c]
    }
  }
  return res
}

// 移动处理：通过旋转棋盘复用向左移动逻辑
const moveLocked = () => isGameOver.value || (isWon.value && !keepPlaying.value)

const isFormField = (event: KeyboardEvent) => {
  const el = event.target
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable
}

const move = (direction: 'left' | 'up' | 'right' | 'down') => {
  if (moveLocked()) return

  let rotations = 0
  if (direction === 'up') rotations = 3
  else if (direction === 'right') rotations = 2
  else if (direction === 'down') rotations = 1

  let temp = board.value
  for (let i = 0; i < rotations; i++) {
    temp = rotateBoard(temp)
  }

  let movedAny = false
  let totalGain = 0
  let nextBoard: number[][] = []

  for (let r = 0; r < 4; r++) {
    const { newRow, gainedScore, moved } = slideAndMergeRow(temp[r])
    nextBoard.push(newRow)
    totalGain += gainedScore
    if (moved) movedAny = true
  }

  // 转回原方向
  const backRotations = (4 - rotations) % 4
  for (let i = 0; i < backRotations; i++) {
    nextBoard = rotateBoard(nextBoard)
  }

  if (movedAny) {
    moveCount++
    board.value = nextBoard
    score.value += totalGain
    updateBestScore()
    addRandomTile()
    checkGameOver()
  }
}

// 检查游戏是否结束
const checkGameOver = () => {
  // 1. 还有空位则未结束
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      if (board.value[r][c] === 0) return
    }
  }

  // 2. 检查是否有相邻相同项可合并
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      const val = board.value[r][c]
      if (r + 1 < 4 && board.value[r + 1][c] === val) return
      if (c + 1 < 4 && board.value[r][c + 1] === val) return
    }
  }

  isGameOver.value = true
  updateBestScore()
  submitScore(score.value)
}

// 键盘事件监听
const directionByKey: Record<string, 'left' | 'up' | 'right' | 'down'> = {
  ArrowLeft: 'left',
  a: 'left',
  A: 'left',
  ArrowUp: 'up',
  w: 'up',
  W: 'up',
  ArrowRight: 'right',
  d: 'right',
  D: 'right',
  ArrowDown: 'down',
  s: 'down',
  S: 'down',
}

const handleKeyDown = (e: KeyboardEvent) => {
  if (isFormField(e) || moveLocked()) return
  const direction = directionByKey[e.key]
  if (!direction) return
  e.preventDefault()
  move(direction)
}

// 移动端 Touch 触控支持
const handleTouchStart = (e: TouchEvent) => {
  if (e.touches.length > 0) {
    touchStartX = e.touches[0].clientX
    touchStartY = e.touches[0].clientY
  }
}

const handleTouchMove = (e: TouchEvent) => {
  // 阻止棋盘区域的滚动事件
  e.preventDefault()
}

const handleTouchEnd = (e: TouchEvent) => {
  if (e.changedTouches.length === 0) return

  const deltaX = e.changedTouches[0].clientX - touchStartX
  const deltaY = e.changedTouches[0].clientY - touchStartY
  const absX = Math.abs(deltaX)
  const absY = Math.abs(deltaY)

  // 滑动阈值 25px
  if (Math.max(absX, absY) < 25) return

  if (absX > absY) {
    if (deltaX > 0) move('right')
    else move('left')
  } else {
    if (deltaY > 0) move('down')
    else move('up')
  }
}

// 方块色彩配置
const getTileClass = (val: number): string => {
  switch (val) {
    case 2:
      return 'bg-[#eee4da] text-[#776e65]'
    case 4:
      return 'bg-[#ede0c8] text-[#776e65]'
    case 8:
      return 'bg-[#f2b179] text-white shadow-xs'
    case 16:
      return 'bg-[#f59563] text-white shadow-xs'
    case 32:
      return 'bg-[#f67c5f] text-white shadow-xs'
    case 64:
      return 'bg-[#f65e3b] text-white shadow-xs'
    case 128:
      return 'bg-[#edcf72] text-white shadow-md'
    case 256:
      return 'bg-[#edcc61] text-white shadow-md'
    case 512:
      return 'bg-[#edc850] text-white shadow-md'
    case 1024:
      return 'bg-[#edc53f] text-white shadow-lg'
    case 2048:
      return 'bg-[#edc22e] text-white shadow-[0_0_12px_rgba(237,194,46,0.8)]'
    default:
      return 'bg-[#3c3a32] text-white shadow-lg'
  }
}

onMounted(async () => {
  initBestScore()
  resetGame()
  window.addEventListener('keydown', handleKeyDown)

  try {
    const res = await getPlayerProgress('game_2048')
    if (res.data?.highestLevel && res.data.highestLevel > bestScore.value) {
      bestScore.value = res.data.highestLevel
      try {
        localStorage.setItem(STORAGE_KEY, String(bestScore.value))
      } catch (e) {}
    } else if (bestScore.value > (res.data?.highestLevel || 0)) {
      submitScore(bestScore.value)
    }
  } catch (e) {
    // 容错保护
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
})
</script>
